import { useEffect, useState } from 'react'

/**
 * Generic delay hook that triggers after a specified condition is met
 * Useful for preventing immediate UI flashes or implementing delayed visibility
 * 
 * @param shouldTrigger - Boolean condition that determines when to start the delay
 * @param delayMs - Delay in milliseconds before returning true (default: 2000ms)
 * @returns boolean indicating whether the delay has elapsed and condition should be shown
 */
export const useDelay = (
    shouldTrigger: boolean,
    delayMs: number = 2000
): boolean => {
    const [showDelayed, setShowDelayed] = useState(false)

    useEffect(() => {
        let timer: NodeJS.Timeout

        if (shouldTrigger) {
            // Start timer when trigger condition is met
            timer = setTimeout(() => {
                setShowDelayed(true)
            }, delayMs)
        } else {
            // Reset the delayed state if trigger condition changes
            setShowDelayed(false)
        }

        return () => {
            if (timer) clearTimeout(timer)
        }
    }, [shouldTrigger, delayMs])

    return showDelayed
}